/**
 * Tell sessions a change was taken back (RFC 0313 3f).
 *
 * One writer for both undo and restore, so the event reads the same whichever
 * way the change went. NEVER THROWS: the undo already happened, and a session
 * log that cannot be reached is a reason to tell nobody, not to fail the undo.
 *
 * WHO IS TOLD: the session that made the change, and every session holding the
 * document (RFC 0297), because the session most likely to redo an undone edit
 * is the one working on the document now, which need not be its author (paired
 * review of PR #329). Sessions not holding it stay quiet.
 *
 * AT MOST ONCE PER SESSION. A session already told about this undo is skipped,
 * so a retry after an interruption repairs a missing notice and never doubles
 * one (0313 adversarial review, finding 9).
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import type { ProjectDoc } from '../project/types'
import type { HistoryChange } from './history'

export interface UndoneBy {
  /** The person's `userDocId`, or the session that did it. */
  id: string
  name?: string
  /** Set when a session undid: it is not told about its own undo. */
  sessionId?: string
}

/** What a row did, in a line, for the event and the card. */
export function undoneSummary(row: HistoryChange): string {
  const clip = (s: string) => {
    const one = s.replace(/\s+/g, ' ').trim()
    return one.length > 80 ? `${one.slice(0, 79)}…` : one
  }
  if (row.text) {
    const { added, removed } = row.text
    if (added && removed) return `“${clip(removed)}” → “${clip(added)}”`
    if (added) return `added “${clip(added)}”`
    return `removed “${clip(removed)}”`
  }
  if (row.fields?.length)
    return `changed ${row.fields.map((f) => f.path).join(', ')}`
  return 'a change'
}

/** The sessions in a project whose holdings include this document. */
export async function sessionsHolding(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<string[]> {
  try {
    const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
    await (project as { whenReady?: () => Promise<void> }).whenReady?.()
    const meta = (project.doc() as { sessionMeta?: unknown } | undefined)
      ?.sessionMeta as Record<string, { holding?: unknown }> | undefined
    if (!meta) return []
    return Object.entries(meta)
      .filter(
        ([, m]) =>
          Array.isArray(m?.holding) &&
          (m.holding as unknown[]).some((id) => String(id) === documentId),
      )
      .map(([id]) => id)
  } catch {
    return []
  }
}

export async function tellUndone(
  repo: Repo,
  sessionIds: Iterable<string>,
  event: {
    documentId: string
    reverts?: string
    restores?: string[]
    by: UndoneBy
    summary: string
  },
): Promise<void> {
  for (const sessionId of new Set(sessionIds)) {
    if (!sessionId || sessionId === event.by.sessionId) continue
    try {
      const session = await repo.find(sessionId as AnyDocumentId)
      await (session as { whenReady?: () => Promise<void> }).whenReady?.()
      const log = new BranchLog(session as never)
      if (log.hasUndone(event.documentId, event.reverts, event.restores))
        continue
      log.appendUndone({
        documentId: event.documentId,
        ...(event.reverts ? { reverts: event.reverts } : {}),
        ...(event.restores ? { restores: event.restores } : {}),
        by: event.by.id,
        ...(event.by.name ? { byName: event.by.name } : {}),
        summary: event.summary,
      })
    } catch {
      // See above.
    }
  }
}
